<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="tag-applied-modal-goal"
            :is-open="isOpen"
            :goal-tip-link="toolTipLink"
            :goal-tip-text="$t('funnel.edit.goal.tag.tip.new')"
            :summary-text="$t('funnel.edit.goal.tag.logic.new')"
            :title="$t('funnel.edit.tag.title')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <DsInlineAlert
                    v-if="inlineAlertText && !isNewTagVisible"
                    leading-icon
                    class="inline-alert"
                    type="critical"
                >
                    <template #default>
                        <span>{{ inlineAlertText }}</span>
                    </template>
                </DsInlineAlert>

                <DsMultiselect
                    v-model="selectedTags"
                    data-qa="tag-applied-tags"
                    multiple
                    searchable
                    label-prop="label"
                    value-prop="id"
                    :placeholder="$t('funnel.edit.goal.tag.contact.group.placeholder')"
                    :options="getTags"
                    allow-add
                    @add="openAddNewTag"
                />

                <DsRadioGroup
                    v-if="isAchievementTypeVisible"
                    v-model="selectedAchievementType"
                    data-qa="tag-applied-achievement"
                    :options="achievementTypes"
                />

                <span v-if="isCategoriesVisible" class="summary-text">{{ $t('funnel.edit.goal.tag.and.or.logic') }}<br /><br /></span>

                <DsMultiselect
                    v-if="isCategoriesVisible"
                    v-model="selectedCategories"
                    data-qa="tag-applied-categories"
                    multiple
                    searchable
                    label-prop="label"
                    value-prop="id"
                    :placeholder="$t('funnel.edit.goal.tag.category.placeholder')"
                    :options="getCategories"
                />

                <DsModal
                    data-qa="new-tag-popup"
                    :title="$t('funnel.edit.tag.addTag')"
                    :is-open="isNewTagVisible"
                    show-close
                    size="xs"
                    @close="closeTagModal"
                >
                    <DsInlineAlert
                        v-if="inlineAlertText"
                        leading-icon
                        type="critical"
                    >
                        {{ inlineAlertText }}
                    </DsInlineAlert>

                    <DsInputField
                        v-model.trim="newTagName"
                        required
                        data-qa="new-tag-name"
                        class="input-field"
                        type="text"
                        :label="$t('funnel.edit.tag.name.label')"
                    />

                    <DsMultiselect
                        v-model="selectedCategoryNew"
                        data-qa="new-tag-applied-categories-add"
                        label-prop="label"
                        value-prop="id"
                        searchable
                        :placeholder="$t('funnel.edit.tag.category.label')"
                        :options="categories"
                    />

                    <DsInputField
                        v-if="isNewCategoryVisible"
                        v-model.trim="newCategoryName"
                        data-qa="new-category-name"
                        class="input-field"
                        type="text"
                        :label="$t('funnel.edit.tag.category.name.label')"
                    />

                    <DsButton data-qa="save-button" @click="addNewTag">
                        {{ $t('funnel.edit.tag.save') }}
                    </DsButton>
                </DsModal>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';

import {
    loadTags,
    loadCategories,
    createNewTag,
    createNewCategory,
} from './tag.applied.api';

const TAG_APPLIED_CONSTANTS = {
    TYPE_ALL: 'all',
    TYPE_ANY: 'any',
};

export default {
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    tagLabel: this.$t('funnel.edit.goal.tag.contact.group.placeholder'),
                    tags: [],
                    categories: [],
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            inlineAlertText: '',
            tags: [],
            categories: [],
            newTagName: '',
            newCategoryName: '',
            selectedTags: [],
            selectedCategories: [],
            selectedCategoryNew: null,
            isNewTagOpen: false,
            selectedAchievementType: this.cell?.value?.achievementType,
            achievementTypes: [
                { value: TAG_APPLIED_CONSTANTS.TYPE_ALL, label: this.$t('funnel.edit.goal.tag.all.tag') },
                { value: TAG_APPLIED_CONSTANTS.TYPE_ANY, label: this.$t('funnel.edit.goal.tag.any.tag') },
            ],
        };
    },

    computed: {
        getTags() {
            return this.tags?.filter((option) => !this.selectedTags?.find((o) => o.id === option.id));
        },
        getCategories() {
            return this.categories?.filter((option) => !this.selectedCategories?.find((o) => o.id === option.id));
        },
        isAchievementTypeVisible() {
            return this.selectedTags?.length > 1;
        },
        isCategoriesVisible() {
            return !(this.selectedAchievementType === TAG_APPLIED_CONSTANTS.TYPE_ALL);
        },
        isNewCategoryVisible() {
            return this.selectedCategoryNew?.id === -1;
        },
        isNewTagVisible() {
            return this.isNewTagOpen;
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.edit.goal.tag.tip.url.keap') : this.$t('funnel.edit.goal.tag.tip.url.classic');
        },
    },

    watch: {
        selectedCategories() { this.inlineAlertText = ''; },
        selectedTags() { this.inlineAlertText = ''; },
        newTagName() { this.inlineAlertText = ''; },
        newCategoryName() { this.inlineAlertText = ''; },
        isNewTagVisible() { this.inlineAlertText = ''; },
    },

    async mounted() {
        await this.loadTagDropdown();
        await this.loadCategoriesDropdown();
        this.selectedTags = this.getSanitizedArray(this.cell?.value?.tagIds, this.tags);
        this.selectedCategories = this.getSanitizedArray(this.cell?.value?.tagCategoryIds, this.categories);
        this.selectedAchievementType = this.cell?.value?.achievementType ?? TAG_APPLIED_CONSTANTS.TYPE_ANY;
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },
        closeTagModal() {
            this.loadCategoriesDropdown();
            this.isNewTagOpen = false;
        },
        getSanitizedArray(array, data) {
            if (!array) return [];

            const valuesArray = typeof array === 'string' ? array.split(',') : array;

            return valuesArray.map((id) => {
                return {
                    id: id.toString().replace('L', ''),
                    label: data.find((o) => Number(o.id) === Number(id.toString().replace('L', '')))?.label,
                };
            });
        },
        isReady() {
            let valid = true;

            if (!this.selectedTags?.length && !this.selectedCategories?.length) {
                this.inlineAlertText = this.$t('funnel.edit.tag.tagRequiredErr');
                valid = false;
            }

            return valid;
        },

        updateConfig() {
            if (this.selectedAchievementType === TAG_APPLIED_CONSTANTS.TYPE_ALL) {
                this.selectedCategories = [];
            }

            if (this.isReady()) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.tagIds = this.selectedTags.map((element) => { return `${element.id}L`; });
                cell.value.achievementType = this.selectedAchievementType;
                cell.value.tagCategoryIds = this.selectedCategories.map((element) => { return `${element.id}L`; });

                this.$emit('update-cell-value', { cell });
            }
        },


        async createNewCategory() {
            let result = null;

            if (this.newCategoryName) {
                result = await createNewCategory({
                    label: this.newCategoryName,
                    description: this.newCategoryName,
                });
                this.selectedCategoryNew.id = result.data;
                await this.loadCategoriesDropdown();
                this.categories.splice(0, 0, { label: this.$t('funnel.edit.tag.createCategory'), id: Number(-1) });
            }

            return result;
        },

        async addNewTag() {
            this.inlineAlertText = '';
            let result = null;
            const category = this.selectedCategoryNew?.id === -1
                ? { label: this.newCategoryName }
                : this.categories?.find((o) => o.label === this.selectedCategoryNew?.label);
            const tagName = category?.label ? `${category?.label}->${this.newTagName}` : this.newTagName?.length > 0 ? this.newTagName : null;
            const existingElement = this.tags?.find((o) => o.label === tagName);

            if (!existingElement?.label && this.newTagName && ((this.selectedCategoryNew?.id === -1 && this.newCategoryName) || this.selectedCategoryNew?.id !== -1)) {
                if (this.selectedCategoryNew?.id === -1) {
                    result = await this.createNewCategory();
                }

                result = await createNewTag({
                    label: this.newTagName,
                    description: this.newTagName,
                    category: this.selectedCategoryNew?.id,
                });

                if (result?.data) {
                    await this.loadTagDropdown();

                    this.selectedTags.push({ label: `${tagName}`, id: result.data });
                    this.closeTagModal();
                }
            } else {
                if (!this.newTagName) {
                    this.inlineAlertText = this.$t('funnel.edit.tag.name.label.err.required');
                }

                if (!((this.selectedCategoryNew?.id === -1 && this.newCategoryName) || this.selectedCategoryNew?.id !== -1)) {
                    this.inlineAlertText = this.$t('funnel.edit.tag.category.name.label.err.required');
                }

                if (existingElement?.label) {
                    this.inlineAlertText = this.$t('funnel.edit.tag.name.label.err.alreadyExists');
                }
            }
        },

        async openAddNewTag(newTag) {
            await this.loadCategoriesDropdown();
            this.categories.splice(0, 0, { label: this.$t('funnel.edit.tag.createCategory'), id: Number(-1) });
            this.selectedCategoryNew = null;
            this.newTagName = newTag;
            this.newCategoryName = '';
            this.isNewTagOpen = true;
            this.inlineAlertText = null;
        },

        async loadTagDropdown() {
            try {
                this.tags = [];
                const { tagDTOList } = await loadTags();

                Object.keys(tagDTOList).forEach((key) => {
                    if (!tagDTOList[key].name.includes('[DNR]')) {
                        if (tagDTOList[key].name) {
                            if (tagDTOList[key]?.category?.name) {
                                this.tags.push({ label: tagDTOList[key]?.category?.name.concat('->').concat(tagDTOList[key].name), id: tagDTOList[key].id.toString().replace('L', '') });
                            } else {
                                this.tags.push({ label: tagDTOList[key].name, id: tagDTOList[key].id.toString().replace('L', '') });
                            }
                        }
                    }
                });
            } catch (error) {
                this.$error({ message: 'Unable to load tags' });
            }
        },

        async loadCategoriesDropdown() {
            try {
                this.categories = await loadCategories();
            } catch (error) {
                this.$error({ message: 'Unable to load tags' });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.campaign-builder-modal .inline-alert {
    margin-bottom: $spacing-200;
    width: 100%;
}
</style>
